import { useRef, useState, type KeyboardEvent } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { focusRing } from './focus-ring';

/**
 * Ratings (design-spec §3 "Rating stars").
 *
 * <RatingStars variant="inline"> — one accent ★, the number (700) and an
 *   optional "(count)": "★ 4.8 (86)". Used on cards and the salon header.
 * <RatingStars variant="full"> — five stars, accent filled / line empty,
 *   with half stars (value rounded to the nearest 0.5). Used on review cards.
 * Both are read-only and expose a single `role="img"` with
 *   `aria-label="4.8 out of 5"` (locale-formatted).
 *
 * <RatingInput> — a 1–5 picker for the review form (M6). WAI-ARIA radio
 *   group with roving tabindex: ←/↓ lower, →/↑ raise, Home/End jump.
 *
 * Products rule D-04: never render a rating for a salon with zero reviews —
 * show the "New on Salunas" Badge instead.
 */

function useNumberFormat(fractionDigits: number) {
  const { i18n } = useTranslation();
  return (n: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language || 'en', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(n);
}

/** Fill fraction (0, 0.5 or 1) for star `index` (1-based) at `value`. */
export function starFill(value: number, index: number): 0 | 0.5 | 1 {
  const rounded = Math.round(value * 2) / 2;
  if (rounded >= index) return 1;
  if (rounded >= index - 0.5) return 0.5;
  return 0;
}

function StarGlyph({ fill, size }: { fill: 0 | 0.5 | 1; size: number }) {
  return (
    <span
      className="relative inline-block shrink-0"
      style={{ width: size, height: size }}
      data-fill={fill}
    >
      <Star
        className="absolute inset-0 fill-sl-line text-sl-line"
        style={{ width: size, height: size }}
        strokeWidth={0}
      />
      {fill > 0 && (
        <span
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: fill === 1 ? size : size / 2 }}
        >
          <Star
            className="fill-sl-accent text-sl-accent"
            style={{ width: size, height: size }}
            strokeWidth={0}
          />
        </span>
      )}
    </span>
  );
}

export interface RatingStarsProps {
  /** 0–5. */
  value: number;
  /** Review count shown as "(86)" in the inline variant. */
  count?: number;
  variant?: 'inline' | 'full';
  /** Star size in px (spec: 12–14). */
  size?: number;
  className?: string;
}

export function RatingStars({
  value,
  count,
  variant = 'inline',
  size,
  className,
}: RatingStarsProps) {
  const { t } = useTranslation();
  const fmt = useNumberFormat(1);
  const clamped = Math.max(0, Math.min(5, value));
  const label = t('ui.rating.label', { value: fmt(clamped) });

  if (variant === 'full') {
    const px = size ?? 12;
    return (
      <span
        role="img"
        aria-label={label}
        className={cn('inline-flex items-center gap-[2px]', className)}
      >
        {[1, 2, 3, 4, 5].map((i) => (
          <StarGlyph key={i} fill={starFill(clamped, i)} size={px} />
        ))}
      </span>
    );
  }

  const px = size ?? 13;
  const fullLabel =
    count !== undefined
      ? `${label}, ${t('ui.rating.reviews', { count })}`
      : label;
  return (
    <span
      role="img"
      aria-label={fullLabel}
      className={cn(
        'inline-flex items-center gap-1 font-sans text-[13.5px] text-sl-body-2',
        className
      )}
    >
      <StarGlyph fill={1} size={px} />
      <span className="font-bold text-sl-ink">{fmt(clamped)}</span>
      {count !== undefined && <span>({count})</span>}
    </span>
  );
}

export interface RatingInputProps {
  value: number | null;
  onChange: (value: number) => void;
  /** Accessible group label (defaults to "Your rating"). Ignored when `labelledBy` is set. */
  label?: string;
  /** Id of a visible label for the group (avoids a second, duplicate name). */
  labelledBy?: string;
  /** Marks the group invalid (e.g. a required rating is missing). */
  invalid?: boolean;
  /** Id of the error/help text. */
  describedBy?: string;
  size?: number;
  disabled?: boolean;
  className?: string;
}

export function RatingInput({
  value,
  onChange,
  label,
  labelledBy,
  invalid,
  describedBy,
  size = 28,
  disabled,
  className,
}: RatingInputProps) {
  const { t } = useTranslation();
  const [hover, setHover] = useState<number | null>(null);
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const shown = hover ?? value ?? 0;
  // Roving tabindex: the checked star, or the first one when nothing is picked.
  const tabStop = value ?? 1;

  const select = (n: number) => {
    if (disabled) return;
    const next = Math.max(1, Math.min(5, n));
    onChange(next);
    refs.current[next - 1]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, n: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = n + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = n - 1;
    else if (e.key === 'Home') next = 1;
    else if (e.key === 'End') next = 5;
    if (next !== null) {
      e.preventDefault();
      select(next);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={labelledBy ? undefined : (label ?? t('ui.rating.inputLabel'))}
      aria-labelledby={labelledBy}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-disabled={disabled || undefined}
      className={cn('inline-flex items-center gap-1', className)}
      onMouseLeave={() => setHover(null)}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          ref={(el) => {
            refs.current[n - 1] = el;
          }}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={t('ui.rating.star', { count: n })}
          tabIndex={n === tabStop ? 0 : -1}
          disabled={disabled}
          onClick={() => select(n)}
          onKeyDown={(e) => onKeyDown(e, n)}
          onMouseEnter={() => !disabled && setHover(n)}
          className={cn(
            'flex h-touch w-touch items-center justify-center rounded-full disabled:cursor-not-allowed',
            focusRing,
          )}
        >
          <StarGlyph fill={n <= shown ? 1 : 0} size={size} />
        </button>
      ))}
    </div>
  );
}
